import { useState, useEffect, type ReactNode } from "react";
import {
  HarvestLogContext,
  type HarvestLogContextType,
} from "./HarvestLogContext";
import type { HarvestLog } from "../utils/types";
import {
  callFetchHarvestLogs,
  callCreateHarvestLog,
  callDeleteHarvestLog,
  callDeleteAllHarvestLogs,
} from "../../../api/harvestLogs";
import { useAuthUser } from "../hooks/useAuthUser";

interface HarvestLogProviderProps {
  children: ReactNode;
}

export const HarvestLogContextProvider = ({
  children,
}: HarvestLogProviderProps) => {
  const { isAdmin, isEmployee } = useAuthUser();
  const [harvestLogs, setHarvestLogs] = useState<HarvestLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const canAccessHarvestLogs = isAdmin || isEmployee;

  const fetchHarvestLogs = async (): Promise<void> => {
    try {
      setLoading(true);
      setError(null);
      const data = await callFetchHarvestLogs();
      setHarvestLogs(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error fetching harvest logs:", err);
      setError(
        err instanceof Error ? err.message : "Failed to fetch harvest logs",
      );
      setHarvestLogs([]);
    } finally {
      setLoading(false);
    }
  };

  const createLog = async (log: HarvestLog): Promise<void> => {
    try {
      setLoading(true);
      setError(null);
      const endDate = log.endDate === "" ? log.startDate : log.endDate;

      const created = await callCreateHarvestLog({
        startDate: log.startDate,
        endDate: endDate,
        comment: log.comment,
        products: log.products.map(({ product, quantity }) => ({
          product,
          quantity,
        })),
      });
      setHarvestLogs((prev) => [created, ...prev]);
    } catch (err) {
      console.error("Error adding harvest log:", err);
      setError(
        err instanceof Error ? err.message : "Failed to add harvest log",
      );
    } finally {
      setLoading(false);
    }
  };

  const deleteLog = async (id: string): Promise<void> => {
    try {
      setLoading(true);
      setError(null);
      await callDeleteHarvestLog(id);
      setHarvestLogs((prev) => prev.filter((log) => log.logId !== id));
    } catch (err) {
      console.error("Error deleting harvest log:", err);
      setError(
        err instanceof Error ? err.message : "Failed to delete harvest log",
      );
    } finally {
      setLoading(false);
    }
  };

  const deleteAllLogs = async (): Promise<void> => {
    try {
      setLoading(true);
      setError(null);
      await callDeleteAllHarvestLogs();
      setHarvestLogs([]);
    } catch (err) {
      console.error("Error deleting all harvest logs:", err);
      setError(
        err instanceof Error
          ? err.message
          : "Failed to delete all harvest logs",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!canAccessHarvestLogs) {
      setHarvestLogs([]);
      setError(null);
      setLoading(false);
      return;
    }
    fetchHarvestLogs();
  }, [canAccessHarvestLogs]);

  const value: HarvestLogContextType = {
    harvestLogs,
    loading,
    error,
    createLog,
    deleteLog,
    deleteAllLogs,
  };

  return (
    <HarvestLogContext.Provider value={value}>
      {children}
    </HarvestLogContext.Provider>
  );
};
